import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Box, Button, Stack, Typography } from '@mui/material';
import { BarChart, DirectionsRun, Schedule, LocalFireDepartment } from '@mui/icons-material';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import { MY_PLAY_PROFILE, MY_PLAY_WORKOUTS, MY_PLAY_WORKOUT_SUMMARY } from 'api/graphql/play';
import { muscleLabel } from 'components/play/constants';
import { formatDuration } from 'components/play/workoutEstimate';
import routes from 'common/routes';

// Suficiente para encontrar el último completado sin traerse el historial entero.
const RECENT_WORKOUTS = 10;

const fmtDate = (iso) => {
  try {
    return format(parseISO(iso), 'd MMM yyyy', { locale: es });
  } catch {
    return '';
  }
};

/**
 * OTRIX Play — "Rendimiento": el último entrenamiento completado de un vistazo.
 *
 * Reemplaza al antiguo tile de Ranking: Play dejó de ser una competencia, así que el home muestra
 * TU último trabajo (músculo, duración, series) en vez de una posición contra otros.
 */
function PlayLastWorkoutTile() {
  const { t } = useTranslation(['play']);
  const navigate = useNavigate();

  const { data } = useQuery(MY_PLAY_WORKOUTS, {
    variables: { first: RECENT_WORKOUTS },
    fetchPolicy: 'cache-and-network'
  });
  // Racha + sesiones de la semana: heredadas de la card "Tu semana" (eliminada por
  // redundante con la tira de días); el backend ya calcula ambas en el summary.
  const { data: sumData } = useQuery(MY_PLAY_WORKOUT_SUMMARY, { fetchPolicy: 'cache-and-network' });
  const { data: profileData } = useQuery(MY_PLAY_PROFILE, { fetchPolicy: 'cache-and-network' });

  const summary = sumData?.myPlayWorkoutSummary || null;
  const streakDays = summary?.current_streak_days || 0;
  const completedThisWeek = summary?.completed_this_week || 0;
  const daysPerWeek = profileData?.myPlayProfile?.days_per_week || null;

  // `myPlayWorkouts` no filtra por estado ni garantiza orden: se resuelve aquí (mismo criterio
  // que "Tu semana").
  const last = useMemo(() => {
    const done = (data?.myPlayWorkouts || []).filter((w) => w.status === 'completed' && w.completed_at);
    return [...done].sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at))[0] || null;
  }, [data]);

  const stats = last
    ? [
        { icon: Schedule, value: formatDuration(last.total_seconds) },
        { icon: DirectionsRun, value: t('home.lastWorkout.sets', { ns: 'play', count: last.total_sets || 0 }) },
        ...(last.estimated_calories
          ? [{ icon: LocalFireDepartment, value: t('todayCard.kcal', { ns: 'play', count: last.estimated_calories }) }]
          : [])
      ]
    : [];

  return (
    <PremiumCard sx={{ height: '100%' }} innerSx={{ height: '100%' }}>
      <Stack spacing={2} sx={{ height: '100%' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ color: 'gold.main', display: 'flex', flex: 'none' }}>
            <BarChart fontSize="small" />
          </Box>
          <Typography variant="subtitle2" fontWeight={800} sx={{ flex: 1, minWidth: 0 }} noWrap>
            {t('home.lastWorkout.title', { ns: 'play' })}
          </Typography>
          <Button
            size="small"
            onClick={() => navigate(routes.playPerformance.path)}
            sx={{ color: 'gold.main', minWidth: 0, fontWeight: 700, flex: 'none' }}>
            {t('home.viewMore', { ns: 'play' })}
          </Button>
        </Stack>

        {/* Racha + sesiones de la semana (compactas, sin card propia). */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              border: '1px solid',
              borderColor: 'hairline.main',
              backgroundColor: 'surface.overlay'
            }}>
            <LocalFireDepartment sx={{ fontSize: 15, color: 'gold.main' }} />
            <Typography variant="caption" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {t('perfSummary.days', { ns: 'play', count: streakDays })}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              border: '1px solid',
              borderColor: 'hairline.main',
              backgroundColor: 'surface.overlay'
            }}>
            <DirectionsRun sx={{ fontSize: 15, color: 'gold.main' }} />
            <Typography variant="caption" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {daysPerWeek
                ? t('home.lastWorkout.weekProgress', {
                    ns: 'play',
                    done: completedThisWeek,
                    total: daysPerWeek,
                    defaultValue: '{{done}}/{{total}} esta semana'
                  })
                : t('home.lastWorkout.weekCount', {
                    ns: 'play',
                    count: completedThisWeek,
                    defaultValue: '{{count}} esta semana'
                  })}
            </Typography>
          </Stack>
        </Stack>

        {last ? (
          <Stack spacing={1.5} sx={{ flex: 1 }}>
            <Stack spacing={0.25}>
              <Typography variant="h6" fontWeight={800} noWrap>
                {last.muscle_focus ? muscleLabel(t, last.muscle_focus) : t('home.lastWorkout.generic', { ns: 'play' })}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {fmtDate(last.completed_at)}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 'auto' }}>
              {stats.map(({ icon: Icon, value }) => (
                <Stack key={value} direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
                  <Icon sx={{ fontSize: 15, color: 'gold.main' }} />
                  <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                    {value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={0.5} sx={{ flex: 1, justifyContent: 'center' }}>
            <Typography variant="body2" fontWeight={700}>
              {t('home.lastWorkout.emptyTitle', { ns: 'play' })}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('home.lastWorkout.emptyBody', { ns: 'play' })}
            </Typography>
          </Stack>
        )}
      </Stack>
    </PremiumCard>
  );
}

export default PlayLastWorkoutTile;
